/*
 * Authentication Contexts
 * 
 * Provides authentication state management for both users and admins
 * 
 * CONTAINS:
 * - AuthProvider: User authentication context
 * - AdminAuthProvider: Admin authentication context  
 * - Custom hooks: useAuth(), useAdminAuth()
 * 
 * TO EDIT AUTHENTICATION:
 * - Login/logout logic: Modify the respective provider functions
 * - Token storage: Update localStorage keys if needed
 * - API endpoints: Modify API calls in login functions
 */

import React, { useState, useEffect, createContext, useContext } from 'react';
import axios from 'axios';
import { API } from '../utils/config';

// ============================================================================
// USER AUTHENTICATION CONTEXT
// ============================================================================

const AuthContext = createContext();

/**
 * User Authentication Provider
 * Manages regular user login state, JWT tokens, and authentication methods
 * Used throughout the app to check login status and manage user sessions
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(
    localStorage.getItem('token') || sessionStorage.getItem('token')
  );
  const [loading, setLoading] = useState(true);

  // Initialize authentication state on app load
  useEffect(() => {
    if (token) {
      // Set default authorization header for all API requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  /**
   * Fetch current user information using stored JWT token
   */
  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      logout(); // Token is invalid, logout user
    } finally {
      setLoading(false);
    }
  };

  /**
   * Login user with email and password
   * @param {string} email - User email address
   * @param {string} password - User password
   * @returns {Object} Success status and error message if applicable
   */
  const login = async (email, password, rememberMe = false) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { 
        email, 
        password, 
        remember_me: rememberMe 
      });
      
      const { access_token, user: userData, remember_me: isRememberMe } = response.data;
      
      setToken(access_token);
      setUser(userData);
      
      // Store token based on remember me preference
      if (isRememberMe) {
        // Store in localStorage for persistent login (30 days)
        localStorage.setItem('token', access_token);
        localStorage.setItem('rememberMe', 'true');
      } else {
        // Store in sessionStorage for session-only login
        sessionStorage.setItem('token', access_token);
        localStorage.removeItem('rememberMe');
      }
      
      // Set axios default header
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  /**
   * Register new user with email and password
   * @param {string} email - User email address
   * @param {string} password - User password
   * @returns {Object} Success status and error message if applicable
   */
  const register = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/register`, { email, password });
      return { 
        success: true,
        message: response.data.message,
        email:   response.data.email,
        verification_required: response.data.verification_required
      };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  /**
   * Logout user and clear authentication data
   */
  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('rememberMe');
    sessionStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
  };

  const value = {
    user,
    login,
    register,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Custom hook to access user authentication context
 * @returns {Object} Authentication methods and state
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// ============================================================================
// ADMIN AUTHENTICATION CONTEXT
// ============================================================================

const AdminAuthContext = createContext();

/**
 * Admin Authentication Provider
 * Separate authentication system for admin panel access
 * Maintains separate token storage and API endpoints
 */
export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [adminToken, setAdminToken] = useState(localStorage.getItem('adminToken'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (adminToken) {
      // Set authorization header for admin requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${adminToken}`;
      // Note: Could add admin token verification here if needed
    }
    setLoading(false);
  }, [adminToken]);

  /**
   * Login admin with email and password
   * @param {string} email - Admin email address
   * @param {string} password - Admin password
   * @returns {Object} Success status and error message if applicable
   */
  const adminLogin = async (email, password) => {
    try {
      const response = await axios.post(`${API}/admin/auth/login`, { email, password });
      const { access_token, admin: adminData } = response.data;
      
      // Store admin token and data separately from user auth
      setAdminToken(access_token);
      setAdmin(adminData);
      localStorage.setItem('adminToken', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Admin login failed' 
      };
    }
  };

  /**
   * Logout admin and clear admin authentication data
   */
  const adminLogout = () => {
    setAdmin(null);
    setAdminToken(null);
    localStorage.removeItem('adminToken');
    delete axios.defaults.headers.common['Authorization'];
  };

  /**
   * Create new admin user (requires current admin authentication)
   * @param {Object} adminData - New admin user data
   * @returns {Object} Success status and error message if applicable
   */
  const createAdminUser = async (adminData) => {
    try {
      const response = await axios.post(`${API}/admin/users`, adminData);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Failed to create admin user' 
      };
    }
  };

  const value = {
    admin,
    adminLogin,
    adminLogout,
    createAdminUser,
    loading
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

/**
 * Custom hook to access admin authentication context
 * @returns {Object} Admin authentication methods and state
 */
export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};